import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { BaseModal, RadioButton } from '@/components/residents/marketplace/shared';
import { CORK_BOARD_REPORT_REASONS, CORK_BOARD_INPUT_LIMITS } from '@/constants/residents/corkBoardConstants';
import { SEMANTIC_COLORS, PRIMITIVE_COLORS, COMPONENT_COLORS } from '@/constants/residents/residentColors';
import type { ReportReason } from '@/types/residents/corkBoard';

interface CorkBoardReportModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (reason: ReportReason, details?: string) => void;
  isSubmitting: boolean;
  hasAlreadyReported: boolean;
  contentType: 'thread' | 'reply';
}

export const CorkBoardReportModal: React.FC<CorkBoardReportModalProps> = ({
  visible,
  onClose,
  onSubmit,
  isSubmitting,
  hasAlreadyReported,
  contentType,
}) => {
  const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');

  const handleSubmit = useCallback(() => {
    if (selectedReason && !isSubmitting) {
      onSubmit(selectedReason, details.trim() || undefined);
      setSelectedReason(null);
      setDetails('');
    }
  }, [selectedReason, details, isSubmitting, onSubmit]);

  const handleClose = useCallback(() => {
    setSelectedReason(null);
    setDetails('');
    onClose();
  }, [onClose]);

  if (hasAlreadyReported) {
    const footer = (
      <TouchableOpacity style={styles.submitButton} onPress={handleClose} activeOpacity={0.8}>
        <Text style={styles.submitButtonText}>Close</Text>
      </TouchableOpacity>
    );

    return (
      <BaseModal
        visible={visible}
        onClose={handleClose}
        showHeader
        headerStyle={{ borderBottomWidth: 0 }}
        footer={footer}
      >
        <View style={styles.alreadyReportedContent}>
          <Text style={styles.alreadyReportedTitle}>Report submitted</Text>
          <Text style={styles.alreadyReportedDescription}>
            You have already reported this {contentType}. Our moderation team will review it.
          </Text>
        </View>
      </BaseModal>
    );
  }

  const isDisabled = !selectedReason || isSubmitting;
  const footer = (
    <TouchableOpacity
      style={[styles.submitButton, isDisabled && styles.submitButtonDisabled]}
      onPress={handleSubmit}
      activeOpacity={0.8}
      disabled={isDisabled}
    >
      {isSubmitting ? (
        <ActivityIndicator size="small" color={SEMANTIC_COLORS.text.primary} />
      ) : (
        <Text style={[styles.submitButtonText, isDisabled && styles.submitButtonTextDisabled]}>
          Submit report
        </Text>
      )}
    </TouchableOpacity>
  );

  return (
    <BaseModal
      visible={visible}
      onClose={handleClose}
      showHeader
      headerStyle={{ borderBottomWidth: 0 }}
      footer={footer}
      scrollable
      avoidKeyboard
    >
      <View style={styles.titleSection}>
        <Text style={styles.title}>Report this {contentType}</Text>
        <Text style={styles.subtitle}>
          Why are you reporting this {contentType}? Select the reason that best applies.
        </Text>
      </View>

      <View style={styles.optionsList}>
        {CORK_BOARD_REPORT_REASONS.map((reason) => (
          <TouchableOpacity
            key={reason.value}
            style={styles.optionItem}
            onPress={() => setSelectedReason(reason.value)}
            activeOpacity={0.8}
          >
            <Text style={[
              styles.optionLabel,
              selectedReason === reason.value && styles.optionLabelSelected,
            ]}>
              {reason.label}
            </Text>
            <RadioButton isSelected={selectedReason === reason.value} />
          </TouchableOpacity>
        ))}

        <View style={styles.detailsContainer}>
          <TextInput
            style={styles.detailsInput}
            placeholder="Additional details (optional)"
            placeholderTextColor={SEMANTIC_COLORS.text.muted}
            value={details}
            onChangeText={setDetails}
            multiline
            textAlignVertical="top"
            maxLength={CORK_BOARD_INPUT_LIMITS.reportDetailsMax}
          />
        </View>
      </View>
    </BaseModal>
  );
};

const styles = StyleSheet.create({
  titleSection: {
    alignItems: 'center',
    gap: 6,
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 36,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    fontWeight: '400',
    color: SEMANTIC_COLORS.text.secondary,
    lineHeight: 24,
    textAlign: 'center',
  },
  optionsList: {
    gap: 16,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: PRIMITIVE_COLORS.white5,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  optionLabel: {
    fontSize: 14,
    fontWeight: '400',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 21,
    flex: 1,
  },
  optionLabelSelected: {
    fontWeight: '500',
  },
  detailsContainer: {
    backgroundColor: SEMANTIC_COLORS.background.input,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: SEMANTIC_COLORS.border.secondary,
    height: 80,
  },
  detailsInput: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 11,
    paddingBottom: 11,
    fontSize: 14,
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 17.5,
  },
  submitButton: {
    backgroundColor: COMPONENT_COLORS.button.primaryBg,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitButtonDisabled: {
    backgroundColor: COMPONENT_COLORS.button.disabledBg,
  },
  submitButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: COMPONENT_COLORS.button.primaryText,
    lineHeight: 21,
  },
  submitButtonTextDisabled: {
    color: COMPONENT_COLORS.button.disabledText,
  },
  alreadyReportedContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  alreadyReportedTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 36,
    textAlign: 'center',
  },
  alreadyReportedDescription: {
    fontSize: 16,
    fontWeight: '400',
    color: SEMANTIC_COLORS.text.secondary,
    lineHeight: 24,
    textAlign: 'center',
  },
});
